import { Fragment } from "react";
import { Trans, useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  formatDay,
  formatTime,
  groupByDay,
  initials,
  pageCount,
  pageSpan,
  pageWindow,
} from "../door-access-utils";
import DoorEmpty from "./DoorEmpty";
import RetryLabel from "./RetryLabel";

const COLUMNS = ["dateTime", "member", "door", "result", "class"];

const profileUrl = (member) =>
  `/athlete-portal/profile/?athlete=${member.id}&section=dashboard`;

const PersonCell = ({ event, t }) => {
  const name = event.personName || event.member?.name || "";
  if (event.personType === "unmatched") {
    return (
      <div className="door-person">
        <span className="door-avatar door-avatar--ghost">
          <SvgIcon
            src="/img/new-icons/vuesax-linear-user.svg"
            useCurrentColor
          />
        </span>
        {name && <span>{name}</span>}
        <span
          className="door-pill door-pill--subtle"
          data-tone="default"
          title={t("doorAccess.person.unmatchedTip")}
        >
          {t("doorAccess.person.unmatched")}
        </span>
      </div>
    );
  }
  return (
    <div className="door-person">
      <span
        className="door-avatar"
        data-staff={event.personType === "staff" || undefined}
      >
        {initials(name)}
      </span>
      {event.member ? (
        <a className="door-link" href={profileUrl(event.member)}>
          {name}
        </a>
      ) : (
        <span>{name}</span>
      )}
      {event.personType === "staff" && (
        <span className="door-pill door-pill--subtle" data-tone="purple">
          {t("doorAccess.person.staff")}
        </span>
      )}
    </div>
  );
};

export const ResultPill = ({ event, t }) => {
  if (event.result === "opened") {
    return (
      <span className="door-pill" data-tone="success">
        {t("doorAccess.result.opened")}
      </span>
    );
  }
  if (event.result === "emergency") {
    return (
      <span className="door-pill" data-tone="danger">
        {event.reason
          ? t("doorAccess.result.emergencyReason", { reason: event.reason })
          : t("doorAccess.result.emergency")}
      </span>
    );
  }
  return (
    <span className="door-pill" data-tone="warning">
      {event.reason
        ? t("doorAccess.result.deniedReason", { reason: event.reason })
        : t("doorAccess.result.denied")}
    </span>
  );
};

const doorLabel = (door) =>
  door ? [door.name, door.locationName].filter(Boolean).join(" · ") : "–";

const Pagination = ({ page, total, onPage, t }) => {
  const pages = pageCount(total);
  if (pages <= 1) return null;
  return (
    <nav className="door-pagination">
      <button
        type="button"
        aria-label={t("doorAccess.table.previous")}
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        <SvgIcon src="/img/new-icons/chevron-down.svg" useCurrentColor />
      </button>
      {pageWindow(page, pages).map((entry, index, entries) =>
        entry === "gap" ? (
          <span key={`gap-after-${entries[index - 1]}`}>…</span>
        ) : (
          <button
            key={entry}
            type="button"
            aria-current={entry === page ? "page" : undefined}
            onClick={() => onPage(entry)}
          >
            {entry.toLocaleString("en-US")}
          </button>
        )
      )}
      <button
        type="button"
        aria-label={t("doorAccess.table.next")}
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
      >
        <SvgIcon src="/img/new-icons/chevron-down.svg" useCurrentColor />
      </button>
    </nav>
  );
};

/**
 * The Logs table and every state it can be in: waiting for
 * the first event, no results, error + retry, and rows grouped by gym day.
 */
const LogsTable = ({
  logs,
  timeZone,
  page,
  hasEvents,
  filtered,
  onPage,
  onOpenEvent,
  onShowUnmatched,
  onClear,
  onRetry,
}) => {
  const { t } = useTranslation("coaching-portal");
  const data = logs.data;

  const head = (
    <thead>
      <tr>
        {COLUMNS.map((column) => (
          <th key={column}>{t(`doorAccess.table.cols.${column}`)}</th>
        ))}
        <th />
      </tr>
    </thead>
  );

  if (!hasEvents) {
    return (
      <div className="door-table-card">
        <table>{head}</table>
        <DoorEmpty
          big
          tone="info"
          title={t("doorAccess.states.waitingTitle")}
          text={t("doorAccess.states.waitingText")}
        />
      </div>
    );
  }

  if (logs.status === "error") {
    return (
      <div className="door-table-card">
        <DoorEmpty
          icon="/img/new-icons/warning-white.svg"
          tone="danger"
          title={t("doorAccess.states.errorTitle")}
          text={t("doorAccess.states.errorText")}
          actions={
            <button
              type="button"
              data-button-type="secondary"
              onClick={onRetry}
            >
              <RetryLabel />
            </button>
          }
        />
      </div>
    );
  }

  const events = data?.events || [];
  const total = data?.total || 0;
  const span = pageSpan(page, total);
  const groups = groupByDay(events, timeZone);

  return (
    <>
      <div className="door-table-top">
        {data && data.unmatchedCount > 0 && (
          <span className="door-unmatched-link">
            {t("doorAccess.table.unmatchedLink", {
              count: data.unmatchedCount,
            })}
            <button
              type="button"
              className="door-link"
              onClick={onShowUnmatched}
            >
              {t("doorAccess.table.show")}
            </button>
          </span>
        )}
        <span className="door-grow" />
        <span className="door-muted">
          {t("doorAccess.table.timesIn", { zone: timeZone })}
        </span>
      </div>

      <div className="door-table-wrap">
        <div className="door-table-card">
          {total === 0 ? (
            <>
              <table>{head}</table>
              <DoorEmpty
                icon="/img/new-icons/search.svg"
                title={t("doorAccess.states.noResultsTitle")}
                text={t("doorAccess.states.noResultsText")}
                actions={
                  filtered && (
                    <button
                      type="button"
                      data-button-type="secondary"
                      onClick={onClear}
                    >
                      {t("doorAccess.chips.clear")}
                    </button>
                  )
                }
              />
            </>
          ) : (
            <table>
              {head}
              <tbody>
                {groups.map((group) => {
                  const dayLabel = formatDay(group.date, timeZone);
                  let heading = dayLabel;
                  if (group.kind !== "day") {
                    const kindLabel = t(`doorAccess.table.${group.kind}`);
                    heading = `${kindLabel} · ${dayLabel}`;
                  }
                  return (
                    <Fragment key={group.date}>
                      <tr className="door-group-row">
                        <td colSpan={6}>
                          {heading}
                          <span>
                            {t("doorAccess.table.groupCount", {
                              count: group.events.length,
                            })}
                          </span>
                        </td>
                      </tr>
                      {group.events.map((event) => (
                        <tr
                          key={event.id}
                          className="door-row"
                          data-result={event.result}
                          onClick={() => onOpenEvent(event)}
                        >
                          <td>
                            {formatDay(group.date, timeZone)} ·{" "}
                            {formatTime(event.occurredAt, timeZone)}
                          </td>
                          <td>
                            <PersonCell event={event} t={t} />
                          </td>
                          <td>{doorLabel(event.door)}</td>
                          <td>
                            <ResultPill event={event} t={t} />
                          </td>
                          <td className={event.class ? "" : "door-muted"}>
                            {event.class
                              ? `${formatTime(event.class.startsAt, timeZone)} ${event.class.name || ""}`
                              : "–"}
                          </td>
                          <td className="door-row__action">
                            <button
                              type="button"
                              className="door-link"
                              onClick={(clickEvent) => {
                                clickEvent.stopPropagation();
                                onOpenEvent(event);
                              }}
                            >
                              {t("doorAccess.table.details")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
          {total > 0 && (
            <div className="door-table-foot">
              <span>
                <Trans
                  t={t}
                  i18nKey="doorAccess.table.showing"
                  values={{
                    from: span.from,
                    to: span.to,
                    total: total.toLocaleString("en-US"),
                  }}
                  components={{ b: <b /> }}
                />
              </span>
              <span className="door-grow" />
              <Pagination page={page} total={total} onPage={onPage} t={t} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LogsTable;
